import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";

import { useAccessControl } from "../../../app/access-control-context";
import {
  useCanFetchStaffMemberAccess,
  useStaffMemberPermissions,
  useStaffEditabilityCheck
} from "../../../hooks/use-staff";
import { useReferenceData, StaffPermission, ReferenceRecord } from "../../../hooks/use-reference-data";
import { Confirm, ConfirmHandle } from "../../confirm/confirm";
import { LoadingOverlay } from "../../loading-overlay/loading-overlay";
import * as Permissions from "../../../data/permissions";
import { Field, RoleSelector } from "../../forms";
import { StaffPermissionAccordion } from "../../accordion/permissions";

export interface AccessProps {
  staffMemberId: string;
}

export const requiredPermissionForEditingPermissions = Permissions.STAFF_EDIT_PERMISSIONS;

export const Access: React.FC<AccessProps> = ({ staffMemberId }) => {
  const {
    activeCenterId,
    hasPermission,
    profile,
    hasFeatureAccess,
    permissions: currentUserPermissions
  } = useAccessControl();
  const isCenterOwner = currentUserPermissions?.role === "center_owner";

  const isEditingSelf = useMemo(
    () => !!profile?.id && !!staffMemberId && profile.id === staffMemberId,
    [profile, staffMemberId]
  );

  const canFetchStaffAccess = useCanFetchStaffMemberAccess(staffMemberId);

  const showPermissionsEditor = useMemo(
    () => canFetchStaffAccess || Boolean(hasPermission?.(requiredPermissionForEditingPermissions)),
    [canFetchStaffAccess, hasPermission]
  );

  const {
    staffMemberPermissions,
    isLoading: isLoadingStaffMemberPermissions,
    changeRoleAndResetPermissionsMutator,
    permissionMutator,
    checkExclusivePermission
  } = useStaffMemberPermissions(activeCenterId, staffMemberId, { enabled: canFetchStaffAccess });
  const { referenceData, isLoading: isReferenceDataLoading, defaultPermissionsLookup } = useReferenceData();
  const { permissionsDisabled, isRoleImmutable } = useStaffEditabilityCheck(staffMemberId, {
    staffRole: staffMemberPermissions?.role,
    skipPermissionsFetch: true
  });
  const [selectedRole, setSelectedRole] = useState(staffMemberPermissions?.role);

  useEffect(() => {
    setSelectedRole(staffMemberPermissions?.role);
  }, [staffMemberPermissions?.role]);
  const confirmPermissionResetModal = useRef<ConfirmHandle>(null);

  const confirmExclusivePermissionModal = useRef<ConfirmHandle>(null);
  const [exclusiveConfirmTitle, setExclusiveConfirmTitle] = useState("");
  const [exclusiveConfirmMessage, setExclusiveConfirmMessage] = useState<React.ReactNode>(null);

  const onClickResetPermissions = useCallback(() => {
    confirmPermissionResetModal.current &&
      confirmPermissionResetModal.current.confirm(() => {
        changeRoleAndResetPermissionsMutator.mutate({ role: selectedRole });
      });
  }, [selectedRole]);

  const hasEditPermission = useMemo(() => hasPermission?.(requiredPermissionForEditingPermissions), [hasPermission]);

  const [IsInitialPasswordVisible, setIsInitialPasswordVisible] = useState(false);
  const onToggleInitialPassword = useCallback(() => {
    setIsInitialPasswordVisible(previous => !previous);
  }, []);

  if (!showPermissionsEditor) {
    return null;
  }

  if (!referenceData || !staffMemberPermissions || !profile) {
    return null; // delay render until we have these
  }

  const filterTinRefDataPermissions = (permission: ReferenceRecord) => {
    if (!(permission.value as string).startsWith("tin")) {
      return true;
    }
    return !!hasFeatureAccess?.("test_innovators");
  };

  const filterMSFabricRefDataPermissions = (permission: ReferenceRecord) => {
    if (!(permission.value as string).startsWith("fabric")) {
      return true;
    }
    return !!hasFeatureAccess?.("ms_fabric");
  };

  const filteredRefDataPermissions = (permission: StaffPermission) => {
    const filtered = {
      ...permission
    };
    if (filtered.groups?.length) {
      filtered.groups = filtered.groups.map(group => filteredRefDataPermissions(group));
    }

    filtered.permissions = filtered.permissions
      .filter(filterTinRefDataPermissions)
      .filter(filterMSFabricRefDataPermissions);

    return filtered;
  };

  const getExclusivePermissionLabel = (action: string): string => {
    switch (action) {
      case Permissions.MCP_PRIMARY_CENTER_DIRECTOR:
        return "Primary Center Director";
      default:
        return action;
    }
  };

  const handleAllowClick = async (
    action: string,
    isAllowed: boolean,
    isExternalPermission: boolean,
    isExclusivePermission: boolean
  ) => {
    const isMcpSelfAssign = isEditingSelf && action === Permissions.MCP_PRIMARY_CENTER_DIRECTOR;
    if ((!hasEditPermission || permissionsDisabled) && !isMcpSelfAssign) {
      return;
    }

    if (isExclusivePermission && isAllowed) {
      const resp = await checkExclusivePermission.mutateAsync(action);

      if (resp.existingStaffMember) {
        const label = getExclusivePermissionLabel(action);
        setExclusiveConfirmTitle(`Update ${label}`);
        setExclusiveConfirmMessage(
          <>
            <p>
              {resp.existingStaffMember} is currently selected as the {label}.
            </p>
            <p>
              Do you wish to update the {label} to {resp.proposedStaffMember}?
            </p>
          </>
        );

        confirmExclusivePermissionModal.current?.confirm(() => {
          permissionMutator.mutate({ action, isAllowed, isExternalPermission });
        });

        return;
      }
    }

    permissionMutator.mutate({ action, isAllowed, isExternalPermission });
  };

  return (
    <LoadingOverlay
      loading={
        isLoadingStaffMemberPermissions ||
        isReferenceDataLoading ||
        changeRoleAndResetPermissionsMutator.isLoading ||
        permissionMutator.isLoading
      }
    >
      {showPermissionsEditor && (
        <>
          <h3 className="card-subheader">Access</h3>

          <div className="table-header-options">
            <RoleSelector
              mode={!hasEditPermission || (!isEditingSelf && isRoleImmutable) ? "view" : "edit"}
              isEditingSelf={isEditingSelf}
              value={selectedRole ?? ""}
              label="System Access Level"
              onChangeValue={(newvalue: string) => {
                setSelectedRole(newvalue);
              }}
              includeImmutable={true}
              removeEmptyOption
            />
            <button
              className="btn btn-primary"
              onClick={onClickResetPermissions}
              disabled={!hasEditPermission || permissionsDisabled}
            >
              Reset Permissions
            </button>
            <Confirm
              ref={confirmPermissionResetModal}
              title="Confirm Reset To Default Permissions"
              message="Confirming will change the role of the current user and reset all permissions to default role permissions.  Are you sure you wish to continue?"
            />
            <Confirm
              ref={confirmExclusivePermissionModal}
              title={exclusiveConfirmTitle}
              message={exclusiveConfirmMessage}
              cancelLabel="Cancel"
              okLabel="Update"
            />
          </div>

          <StaffPermissionAccordion
            staffMemberPermissions={staffMemberPermissions}
            referenceData={referenceData.permissions
              .map(filteredRefDataPermissions)
              .filter(group => group.permissions?.length || group.groups?.length)
              .sort((a, b) => a.displayName.localeCompare(b.displayName))}
            defaultPermissions={defaultPermissionsLookup[selectedRole ?? ""]}
            onClickAllow={handleAllowClick}
            readOnly={!hasEditPermission || permissionsDisabled}
            canOverrideActions={isEditingSelf ? [Permissions.MCP_PRIMARY_CENTER_DIRECTOR] : []}
            ownerOnlyActions={[Permissions.STAFF_VIEW_POSITION, Permissions.STAFF_EDIT_POSITION]}
            isCenterOwner={isCenterOwner}
          />

          <br />

          <Field
            type={IsInitialPasswordVisible ? "text" : "password"}
            value={staffMemberPermissions.initialPw ?? ""}
            mode={"view"}
            label="Initial password"
            addOnClassName={IsInitialPasswordVisible ? "fas icon fa-eye" : "fas icon fa-eye-slash"}
            onAddOnClick={onToggleInitialPassword}
          />
        </>
      )}
    </LoadingOverlay>
  );
};

export const extractGroupLabel = (input: string) => {
  if (!input || !input.includes(".")) {
    return "";
  } else {
    const value = input.split(".")[0];

    if (value.startsWith("mcp") || value.startsWith("lcn") || value.startsWith("tin")) {
      return value.toUpperCase();
    }

    return value.charAt(0).toUpperCase() + value.slice(1);
  }
};
